import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { Briefcase } from "lucide-react";
import { decodeBriefPlainText, serverFiles } from "utils/utils";
import {
  CREATOR_BRIEF_MARKER,
  briefApplicantsPath,
  isApplicationsClosed,
} from "utils/creatorBriefs";
import ApplyDialog from "components/app/ApplyDialog";
import { useAppliedJobIds } from "utils/appliedJobs";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const stripBriefNoise = (value = "") =>
  decodeBriefPlainText(
    String(value || "")
      .replace(new RegExp(CREATOR_BRIEF_MARKER, "gi"), "")
      .replace(/^jobId:\s*.+$/gim, "")
  );

const fieldFromDescription = (description, label) => {
  const re = new RegExp(`${label}:\\s*([^\\n]+)`, "i");
  return stripBriefNoise(description).match(re)?.[1]?.trim() || null;
};

const needFromProject = (project) => {
  const text = stripBriefNoise(project?.description || "");
  const fromDesc = text
    .split("\n")
    .map((l) => l.trim())
    .find(
      (l) =>
        l &&
          !/^(looking for|work style|city|budget|timeline|project dates|industry|jobid|applicationsclosed):/i.test(
            l
          )
    );
  return stripBriefNoise(fromDesc || project?.jobTitle || "Open brief");
};

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ src, name, size = 40 }) =>
  src ? (
    <img
      src={src}
      alt={name}
      style={{ height: size, width: size }}
      className="rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div
      style={{ height: size, width: size }}
      className="avatar-fallback rounded-full shrink-0 flex items-center justify-center font-semibold text-sm"
    >
      {initials(name)}
    </div>
  );

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-zinc-700/80 bg-zinc-900/80 px-2.5 py-1 text-[0.7rem] font-medium text-zinc-300">
    {children}
  </span>
);

/**
 * Feed / briefs tile for open opportunities (brand jobs + creator collab posts).
 */
const OpenBriefCard = ({ project, onApplied }) => {
  const { userProfile } = useSelector((state) => state.userProfile);
  const { hasApplied } = useAppliedJobIds();
  const [applying, setApplying] = useState(false);

  const isViewerBrand = userProfile?.userType === "BUSINESS";
  const myAlias =
    userProfile?.userAliasId ||
    userProfile?.companyResponseDto?.companyAliasId;
  const isCreatorBrief = !!project?.isCreatorBrief;
  const brand =
    project?.companyJobRespDto?.name || project?.company?.name || "Brand";
  const logoPath =
    project?.companyJobRespDto?.logo || project?.company?.logo;
  const avatarSrc = logoPath ? serverFiles(logoPath) : null;
  const ownerAlias =
    project?.company?.userAliasId ||
    project?.companyJobRespDto?.userAliasId ||
    project?.companyJobRespDto?.companyAliasId;
  const isOwner = !!myAlias && !!ownerAlias && String(myAlias) === String(ownerAlias);
  const profileHref = ownerAlias ? `/user-profile/${ownerAlias}` : "/projects";

  const industry =
    (Array.isArray(project?.industry) && project.industry[0]) ||
    project?.category?.name ||
    fieldFromDescription(project?.description, "Industry");
  const looking =
    (Array.isArray(project?.skillsRequired) &&
      project.skillsRequired.filter(Boolean).join(", ")) ||
    fieldFromDescription(project?.description, "Looking for");
  const workStyle =
    project?.workStyle ||
    fieldFromDescription(project?.description, "Work style");
  const cityRaw =
    fieldFromDescription(project?.description, "City") ||
    project?.cityName ||
    project?.city?.name;
  const city = cityRaw ? String(cityRaw).split(",")[0].trim() : null;
  const budget = formatBudget(project?.projectBudget);
  const timeline =
    fieldFromDescription(project?.description, "Timeline") ||
    (project?.projectDuration != null &&
    String(project.projectDuration).trim() !== "" &&
    Number.isNaN(Number(project.projectDuration))
      ? String(project.projectDuration)
      : null) ||
    (project?.projectDuration != null &&
    !Number.isNaN(Number(project.projectDuration)) &&
    Number(project.projectDuration) > 0
      ? `${project.projectDuration} day${Number(project.projectDuration) === 1 ? "" : "s"}`
      : null);

  const chips = [
    looking && `Looking for ${looking}`,
    workStyle,
    city,
    budget && `Budget ${budget}`,
    timeline,
  ].filter(Boolean);

  const needText = needFromProject(project);
  const applicationsClosed = isApplicationsClosed(project);
  const applied =
    !!project?.isApplied ||
    hasApplied(project?.jobId) ||
    (project?.postId ? hasApplied(`post-${project.postId}`) : false);

  return (
    <>
      <article className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 hover:border-zinc-700 transition-colors">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#8b83ff] font-semibold">
            <Briefcase size={14} /> Open brief
          </div>
          {industry && (
            <span className="rounded-full bg-[#6C63F5]/10 border border-[#6C63F5]/25 text-[#8b83ff] text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-1 shrink-0">
              {String(industry).split(",")[0]}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link to={profileHref} className="shrink-0">
            <Avatar src={avatarSrc} name={brand} />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Link
                to={profileHref}
                className="font-display font-semibold text-zinc-50 tracking-tight truncate hover:text-white"
              >
                {brand}
              </Link>
              <span
                className={`rounded-full border text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                  isCreatorBrief
                    ? "border-[#6C63F5]/35 bg-[#6C63F5]/10 text-[#c9c5ff]"
                    : "border-amber-400/40 bg-amber-400/10 text-amber-300"
                }`}
              >
                {isCreatorBrief ? (isOwner ? "You" : "Creator") : "Brand"}
              </span>
            </div>
          </div>
        </div>

        <p className="mt-4 text-[0.95rem] text-zinc-200 leading-relaxed">
          {needText}
        </p>

        {chips.length > 0 && (
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <Chip key={chip}>{chip}</Chip>
            ))}
          </div>
        )}

        {isOwner ? (
          <Link
            to={briefApplicantsPath(project)}
            className="mt-5 w-full rounded-full border border-zinc-700 bg-zinc-900/70 text-zinc-200 py-2.5 text-sm font-semibold hover:border-zinc-500 transition-colors inline-flex items-center justify-center"
          >
            View applicants
          </Link>
        ) : applied ? (
          <button
            type="button"
            disabled
            className="mt-5 w-full rounded-full bg-[#6C63F5] text-white py-2.5 text-sm font-semibold opacity-40 cursor-not-allowed"
          >
            Applied
          </button>
        ) : isViewerBrand ? (
          <div className="mt-5 rounded-full bg-zinc-900 border border-dashed border-zinc-800 py-2.5 text-xs text-zinc-500 inline-flex w-full items-center justify-center gap-2">
            Only creatives can apply
          </div>
        ) : applicationsClosed ? (
          <button
            type="button"
            disabled
            className="mt-5 w-full rounded-full bg-zinc-800 text-zinc-400 py-2.5 text-sm font-semibold opacity-70 cursor-not-allowed"
          >
            Application Closed
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setApplying(true)}
            className="mt-5 w-full rounded-full bg-[#6C63F5] text-white py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors"
          >
            Apply
          </button>
        )}
      </article>

      <AnimatePresence>
        {applying && (
          <ApplyDialog
            project={project}
            onClose={() => setApplying(false)}
            onApplied={() => {
              setApplying(false);
              onApplied?.();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default OpenBriefCard;
